import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";

/**
 * Load something asynchronously, with a `reload` that re-runs it.
 *
 * The prototype's convention — mutate, then re-render the whole section from
 * fresh state — is worth keeping: it is cheap at this data size and rules out a
 * whole class of stale-view bug. `reload()` is that, in a hook.
 */
export function useAsync<T>(
  load: () => Promise<T>,
  deps: readonly unknown[],
): {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => Promise<void>;
  /** Write straight to local state — takes a value or an updater, like useState. */
  setData: Dispatch<SetStateAction<T | null>>;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const alive = useRef(true);
  const loadRef = useRef(load);
  loadRef.current = load;

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const result = await loadRef.current();
      if (alive.current) {
        setData(result);
        setError(null);
      }
    } catch (e) {
      if (alive.current) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (alive.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    alive.current = true;
    void run();
    return () => {
      alive.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, loading, reload: run, setData };
}

/**
 * Fired by `navigate` when the route it was asked for is the route we are
 * already on.
 *
 * The browser only raises `hashchange` when the hash actually changes, so
 * asking to go somewhere you already are produced silence. From the top search
 * that read as a broken link: searching a customer from inside their own job
 * listed the job, and clicking it did nothing at all — the hash was already
 * `#job=<that id>`. Same for "Back to the job list" from a list. The event
 * gives those clicks something to answer.
 */
const SAME_ROUTE_EVENT = "roofboard:same-route";

/** The current `#hash` route, kept in sync with the address bar. */
export function useHashRoute(): string {
  const [hash, setHash] = useState(() =>
    window.location.hash.replace(/^#/, ""),
  );
  useEffect(() => {
    const update = () => {
      setHash(window.location.hash.replace(/^#/, ""));
      // Going somewhere new means starting at the top of it. Without this a
      // job opened from halfway down a long list opens halfway down.
      window.scrollTo({ top: 0 });
    };
    window.addEventListener("hashchange", update);
    window.addEventListener(SAME_ROUTE_EVENT, update);
    return () => {
      window.removeEventListener("hashchange", update);
      window.removeEventListener(SAME_ROUTE_EVENT, update);
    };
  }, []);
  return hash;
}

export const navigate = (hash: string): void => {
  const current = window.location.hash.replace(/^#/, "");
  if (current === hash) {
    window.dispatchEvent(new Event(SAME_ROUTE_EVENT));
    return;
  }
  window.location.hash = hash;
};

/** Debounce a rapidly-changing value, e.g. a search box. */
export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/**
 * Re-fetch when somebody comes back to the app.
 *
 * Everything the shell shows is loaded once, at sign-in, and again after an
 * action the person took themselves. Nothing has ever asked the server whether
 * somebody else changed something — so a lead a rep created at seven o'clock
 * was invisible to everyone else still holding the app open, and searching for
 * it found nothing. Jonathon, 2026-08-15, having done exactly that.
 *
 * A tab that has been in the background is the moment worth reloading on: it
 * is when the person has been elsewhere and is most likely to be behind, and
 * it costs nothing while they are actually working. The gap stops a reload
 * firing on every alt-tab — coming back from a two-second glance at a text
 * message is the same view, not a stale one.
 */
export function useRefreshOnReturn(
  refresh: () => void | Promise<void>,
  minGapMs = 15_000,
): void {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const lastRun = useRef(Date.now());

  useEffect(() => {
    const returned = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastRun.current < minGapMs) return;
      lastRun.current = now;
      void refreshRef.current();
    };

    // Both, because they do not always come together: switching tabs raises
    // visibilitychange, and clicking back into a window that was behind
    // another app raises focus alone.
    document.addEventListener("visibilitychange", returned);
    window.addEventListener("focus", returned);
    return () => {
      document.removeEventListener("visibilitychange", returned);
      window.removeEventListener("focus", returned);
    };
  }, [minGapMs]);
}

/* ------------------------------------------------- staying where you were --
 *
 * Which list you are on, which rep's tab, which card of a job, which Tools
 * section — none of that is in the address bar, so pressing refresh put you
 * back on the landing screen whatever you had open. Jonathon, 2026-09-20:
 * "when i refresh it keeps taking me away from the page."
 *
 * `sessionStorage`, not `localStorage`: a refresh is the same browser tab and
 * keeps its place, a new tab opens at home, and nothing is still remembered
 * tomorrow — a rep who closes the app should not come back to yesterday's
 * Tools section. It is deliberately not the URL either: a hash is a link
 * somebody can send, and "the screen I happened to be on" is not.
 *
 * Every access is wrapped. Storage throws outright — not returns null — in
 * Safari's private mode and behind some managed-device policies, and a throw
 * here would take the whole screen down to save a convenience. A failure costs
 * the memory and nothing else.
 */

const WHERE_PREFIX = "roofboard:where:";

/** What was remembered under `key` on this browser tab, if anything. */
export function rememberedValue(key: string): string | null {
  try {
    return window.sessionStorage.getItem(WHERE_PREFIX + key);
  } catch {
    return null;
  }
}

/** Remember `value` under `key` for as long as this browser tab is open. */
export function rememberValue(key: string, value: string): void {
  try {
    window.sessionStorage.setItem(WHERE_PREFIX + key, value);
  } catch {
    /* See above: the screen still moves, only the memory of it is lost. */
  }
}

/**
 * `useState` for a string that should survive a refresh.
 *
 * For the screens whose "where am I" is a single value. The shell's is a pair
 * — the list and the rep's tab have to move together — so it keeps its own
 * `rememberedValue` / `rememberValue` calls rather than two of these.
 */
export function useRememberedState(
  key: string,
  initial: string,
  /**
   * A screen the page was *asked* for, which beats the remembered one.
   *
   * Only for the redirect back from somewhere else — Intuit returns to
   * `/tools/quickbooks` with a single-use code, and opening the section
   * somebody happened to leave Tools on would spend it (ADR 0038). It is read
   * once, at mount, like the rest of this hook: a section clicked afterwards
   * still moves.
   */
  override?: string | null,
): [string, (next: string) => void] {
  const [value, setValue] = useState(
    () => override ?? rememberedValue(key) ?? initial,
  );
  const set = useCallback(
    (next: string) => {
      setValue(next);
      rememberValue(key, next);
    },
    [key],
  );
  return [value, set];
}
